import {
  type DocumentNode,
  type OperationVariables,
  useLazyQuery,
} from '@apollo/client';
import { useSnackbar } from 'hephaestus/web';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

function getSearchErrorMessage(
  error: unknown,
  fallbackMessage: string,
): string {
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return fallbackMessage;
}

export function useLazySearchOptions<
  TData,
  TVariables extends OperationVariables,
  TOption,
>({
  query,
  minChars,
  isEnabled = true,
  buildVariables,
  mapResults,
  errorMessageKey,
}: {
  query: DocumentNode;
  minChars: number;
  isEnabled?: boolean;
  buildVariables: (trimmedQuery: string) => TVariables;
  mapResults: (data: TData | undefined) => TOption[];
  errorMessageKey: string;
}): {
  onSearch: (query: string) => Promise<TOption[]>;
  onSearchError: (error?: unknown) => void;
} {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation('portfolioTradingHub');
  const [runSearch] = useLazyQuery<TData, TVariables>(query, {
    fetchPolicy: 'no-cache',
  });

  const fallbackMessage = t(errorMessageKey);

  const onSearchError = useCallback(
    (error?: unknown) => {
      enqueueSnackbar(getSearchErrorMessage(error, fallbackMessage), {
        variant: 'error',
      });
    },
    [enqueueSnackbar, fallbackMessage],
  );

  const onSearch = useCallback(
    async (query: string): Promise<TOption[]> => {
      const trimmed = query.trim();
      if (!isEnabled || trimmed.length < minChars) {
        return [];
      }

      const result = await runSearch({
        variables: buildVariables(trimmed),
      });

      if (result.error) {
        throw result.error;
      }

      return mapResults(result.data);
    },
    [isEnabled, minChars, runSearch, buildVariables, mapResults],
  );

  return { onSearch, onSearchError };
}
